/* eslint-disable max-lines-per-function */
import type { DataInstance } from "@busy-human/gearbox";
import { Dogspaces, Events, Invoices, ScheduleRequest, Userspaces } from "@busy-human/k9-library";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useCalendars } from "./useCalendars";
import { checkAgreementRequirements, generateEventInvoice } from "@/util/functions";
import { useKennelCount } from "./kennelCounts";
import { Temporal } from "temporal-polyfill";
import { useTrainers } from "./useTrainers";
import { useToast } from "vue-toastification";



export const useScheduleRequests = defineStore('schedule-requests', () => {
    const requests = ref(new Map<string, DataInstance<ScheduleRequest.Model>>());
    const loading = ref(false);
    const requestStatus = ref(null as null | string);

    async function reloadRequests() {
        requests.value.clear();
        loading.value = true;
        const docs = await ScheduleRequest.Collection.fetchAll();
        for(const doc of docs.dataItems()) {
            requests.value.set(doc.$id, doc);
        }
        loading.value = false;
    }

    reloadRequests();

    const asArray = computed(() => {
        const arr: DataInstance<ScheduleRequest.Model>[] = [];
        for(const [, req] of requests.value) {
            arr.push(req);
        }
        arr.sort((a, b) => (a.submittedAt || '').localeCompare(b.startTime || ''));
        return arr;
    });

    const pendingRequests = computed(() => asArray.value.filter((req) => !req.reviewedByAdmin));

    async function declineRequest(reqId: string) {
        console.log("Deleting request", reqId);
        requestStatus.value = "Declining request";
        await ScheduleRequest.Collection.deleteDoc(reqId);
        requests.value.delete(reqId);
        console.log("Deleted request", reqId);
    }

    async function sendAgreement(req: DataInstance<ScheduleRequest.Model>){
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const specifiers: Record<any, any> ={
            'dogId': req.dogId, 
            'requestId': req.$id,
        };
        const {data} =  await checkAgreementRequirements({agreementName: 'Canine Revolution Contract', userId: req.userId, specifiers});
        
        if(data.agreementId){
            await ScheduleRequest.Collection.updateDoc(req.$id, {agreementId: req.agreementId});

            return data.agreementId;
        }
        return null;
    }


    type AcceptRequestParams = {
        reqId: string
        req: DataInstance<ScheduleRequest.Model>
        dog: DataInstance<Dogspaces.Model>
        owner: DataInstance<Userspaces.Model>
        discountPercent: number,
        trainerId?: string,
        weeks?: number,
        customPrice?: number
    }
    // eslint-disable-next-line max-params, complexity
    async function acceptRequest({reqId, req, dog, owner, trainerId, weeks, discountPercent, customPrice}: AcceptRequestParams) {
        if(!trainerId && req.eventType === Events.EventType.Training) throw new Error("Not implemented!");
        const calendarStore = useCalendars();
        const trainerStore = useTrainers();
        const kennelStore = useKennelCount();
        await kennelStore.fetchDefaults();
        requestStatus.value = null;
        if(typeof calendarStore.main === 'undefined') throw new Error("Main calendar is not set. Please set a Main calendar in the Manage Calendars tool.");

        if(req.eventType === Events.EventType.Training) {
            if(!weeks) throw new Error("Must specify weeks");

            const price = customPrice || Invoices.EventPricing[req.trainingType]?.[weeks];
            if(typeof price !== 'number') throw new Error(`Price not found for training ${req.eventType} with length ${weeks} weeks`);
            // price /= 2; // Invoices are no longer split into 2 payments

            const description = `Canine Revolution Training - ${Events.TrainingTypeMap[req.trainingType]} - ${weeks} weeks`;
            // -const description2 = `Final Payment - Canine Revolution Training - ${Events.TrainingTypeMap[req.trainingType]} - ${weeks} weeks`;

            const trainer = trainerStore.trainers.get(trainerId || "");
            if(!trainer) throw new Error("Trainer not found");

            //TODO: Instead of creating the agreement here, locate its ID Here
            // requestStatus.value = "Creating Agreement...";
            requestStatus.value = "Grabbing Agreement...";
            const agreementId = req.agreementId;
            console.log("Request Info:", req);

            if(agreementId){
                const evtData = ScheduleRequest.convertToEvent(req, ScheduleRequest.convertWeeksToTimestamp(req.startTime, weeks), dog.name, trainerId);
                // Should probably include this bit with the above function rather than inserting it here. Oh well.
                evtData.title = `${trainer.nickname} ${evtData.title}`;
                evtData.description = `Dog name: ${dog.name}\nOwner name: ${owner.name.fullName}\nOwner contact: ${owner.phone} ${owner.email}`;
                requestStatus.value = "Adding Event...";
                const evt = await calendarStore.addEvent(evtData, "Main");

                requestStatus.value = "Generating Invoice...";
                console.log("About to generate Invoice", discountPercent);
                await generateEventInvoice({agreementId, weeks, event: evt, price, description, discountPercent, dueAt: evt.startTime, partialPayments: true});
            }else{
                throw new Error('Unable to create agreement');
            }
        } else if (req.eventType === Events.EventType.Boarding) {
            if(!useKennelCount().defaultKennelMax) throw new Error("Kennel defaults not set. Please set a maximum kennel count on any date in the Kennel Count calendar");

            const evt1 = ScheduleRequest.convertToEvent(req, req.endTime, dog.name, "Main");
            const [evt2, evt3] = ScheduleRequest.buildTransportEvents(req, dog.name);
            const evtdescription = `Dog name: ${dog.name}\nOwner name: ${owner.name.fullName}\nOwner contact: ${owner.phone} ${owner.email}`;

            evt1.description = evtdescription;
            evt2.description = evtdescription;
            evt3.description = evtdescription;

            const start = new Date(req.startTime);
            const end = new Date(req.endTime);

            const startt = Temporal.PlainDate.from({year: start.getFullYear(), month: start.getMonth()+1, day: start.getDate()});
            const endt = Temporal.PlainDate.from({year: end.getFullYear(), month: end.getMonth()+1, day: end.getDate()});

            const availability = await useKennelCount().kennelSpaceAvailable(startt, endt, dog.size);
            if(!availability.avail) {
                throw new Error(`Training is full for ${dog.size} on the following dates: ${availability.results.filter(r => !r.avail).map(r => r.date).join(", ")}`);
            }

            const length = ScheduleRequest.eventLengthNights(req);
            const price = Invoices.calculatePriceOfBoarding(req);
            const description = `Canine Revolution Boarding - ${length} nights`;

            requestStatus.value = "Adding Boarding Event...";
            const evt = await calendarStore.addEvent(evt1, "Main");

            requestStatus.value = "Creating invoice, updating kennels, adding transport events...";
            const [invoiceRes, kennelRes, pickupRes, dropoffRes] = await Promise.allSettled([
                generateEventInvoice({event: evt, price, description, dueAt: evt.startTime}),
                useKennelCount().addKennel(startt.toString(), endt.toString(), dog.size),
                calendarStore.addEvent(evt2, "Main"),
                calendarStore.addEvent(evt3, "Main")
            ]);

            if(invoiceRes.status === 'rejected') {
                console.error(invoiceRes.reason);
                useToast().error(`Failed to generate invoice: ${invoiceRes.reason}`);
            }
            if(kennelRes.status === 'rejected') {
                console.error(kennelRes.reason);
                useToast().error(`Failed to update kennels: ${kennelRes.reason}`);
            }
            if(pickupRes.status === 'rejected') {
                console.error(pickupRes.reason);
                useToast().error(`Failed to add pickup event: ${pickupRes.reason}`);
            }
            if(dropoffRes.status === 'rejected') {
                console.error(dropoffRes.reason);
                useToast().error(`Failed to add dropoff event: ${dropoffRes.reason}`);
            }

            
        } else {
            throw new Error("Not implemented");
        }

        await ScheduleRequest.Collection.updateDoc(reqId, {reviewedByAdmin: true});
        requests.value.delete(reqId);
    }

    return { requests, loading, reloadRequests, asArray, pendingRequests, declineRequest, acceptRequest, requestStatus, sendAgreement };
});